import type { IWorkbookInspection, IWorkbookInspectionWarning, IWorkbookInspectionWorksheet } from "../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { getSafeToIgnoreNamedRanges } from "./workbookInspectionPlainEnglish";

export const USER_FACING_EXCLUDED_WARNING_CODES = new Set([
    "MergedCellsPresent",
    "FormulaCellsPresent",
    "UnsupportedValidationFormula",
    "ProtectedWorksheet",
    "HiddenWorksheet",
    "VeryHiddenWorksheet",
]);

export function isHiddenWorksheetVisibility(visibility: string): boolean {
    return visibility === "hidden" || visibility === "veryHidden";
}

export function getHiddenWorksheets(worksheets: IWorkbookInspectionWorksheet[]): IWorkbookInspectionWorksheet[] {
    return worksheets.filter((sheet) => isHiddenWorksheetVisibility(sheet.visibility));
}

export function getVisibleWorksheets(worksheets: IWorkbookInspectionWorksheet[]): IWorkbookInspectionWorksheet[] {
    return worksheets.filter((sheet) => !isHiddenWorksheetVisibility(sheet.visibility));
}

export function getProtectionSummaryText(worksheets: IWorkbookInspectionWorksheet[]): string | null {
    const visibleSheets = getVisibleWorksheets(worksheets);
    if (visibleSheets.length === 0) {
        return null;
    }

    const protectedVisibleSheets = visibleSheets.filter((sheet) => sheet.isProtected);
    if (protectedVisibleSheets.length === 0) {
        return null;
    }

    if (protectedVisibleSheets.length === visibleSheets.length) {
        return "All visible worksheets are protected — read-only is fine for import.";
    }

    return `Protected visible worksheets (${protectedVisibleSheets.length}): ${protectedVisibleSheets.map((sheet) => sheet.name).join(", ")} — read-only protection is fine for import.`;
}

export function getHiddenSheetsSummaryText(worksheets: IWorkbookInspectionWorksheet[]): string | null {
    const hiddenSheets = getHiddenWorksheets(worksheets);
    if (hiddenSheets.length === 0) {
        return null;
    }

    const sheetLabel = hiddenSheets.length === 1 ? "sheet" : "sheets";
    return `${hiddenSheets.length} ${sheetLabel} hidden: ${hiddenSheets.map((sheet) => sheet.name).join(", ")}.`;
}

export function shouldDisplayInspectionWarning(
    warning: IWorkbookInspectionWarning,
    safeToIgnoreNamedRangeNames: ReadonlySet<string>
): boolean {
    if (USER_FACING_EXCLUDED_WARNING_CODES.has(warning.code)) {
        return false;
    }

    if (
        warning.code === "BrokenNamedRange" &&
        warning.detail &&
        safeToIgnoreNamedRangeNames.has(warning.detail.toLowerCase())
    ) {
        return false;
    }

    return true;
}

export function dedupeInspectionWarningsBySheetAndCode(
    warnings: IWorkbookInspectionWarning[]
): IWorkbookInspectionWarning[] {
    const seen = new Set<string>();
    const deduped: IWorkbookInspectionWarning[] = [];

    for (const warning of warnings) {
        const key = `${warning.worksheet ?? ""}\0${warning.code}`;
        if (seen.has(key)) {
            continue;
        }

        seen.add(key);
        deduped.push(warning);
    }

    return deduped;
}

export function getSafeToIgnoreNamedRangeNames(inspection: IWorkbookInspection): Set<string> {
    return new Set(getSafeToIgnoreNamedRanges(inspection).map((range) => range.name.toLowerCase()));
}

export function getDisplayedInspectionWarnings(inspection: IWorkbookInspection): IWorkbookInspectionWarning[] {
    const safeToIgnoreNames = getSafeToIgnoreNamedRangeNames(inspection);
    const filtered = inspection.warnings.filter((warning) => shouldDisplayInspectionWarning(warning, safeToIgnoreNames));
    return dedupeInspectionWarningsBySheetAndCode(filtered);
}

export function formatInspectionWarningForDisplay(warning: IWorkbookInspectionWarning): string {
    const location = warning.worksheet ? `${warning.worksheet}: ` : "";
    return `${location}${warning.message}`;
}

export function getDisplayedWarningCount(inspection: IWorkbookInspection): number {
    return getDisplayedInspectionWarnings(inspection).length;
}

export function formatWorksheetVisibility(visibility: string): string {
    if (visibility === "veryHidden") {
        return "Very hidden";
    }

    return visibility.charAt(0).toUpperCase() + visibility.slice(1);
}
